import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar as CalendarIcon, RefreshCw, Pencil } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { EngineerForm } from "@/components/installer/EngineerForm";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { groupDatesToPeriods, formatPeriod, periodKey } from "@/lib/holidayPeriods";
import { effectiveResourceType } from "@/lib/resourceType";
import { DAY_STATE_STYLE, stateForDay, weeklyHoursFor } from "@/lib/availabilityPattern";

/**
 * MVP-ENG-001A · Availability tab content.
 *
 * Shows each engineer's working days and upcoming holidays visibly —
 * no roster table reuse. Read-only for view-only users. `manage_engineers`
 * users get an Edit action per row that opens the existing
 * `EngineerForm` dialog so no editor is duplicated.
 *
 * FIELD-001C1B (clarification) · Holidays render as inclusive periods
 * (e.g. "30 Aug – 5 Sep 2026") — see `@/lib/holidayPeriods`.
 */

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function isoToday() {
    return new Date().toISOString().slice(0, 10);
}

function upcomingHolidayPeriods(list) {
    const today = isoToday();
    // Group first, then filter to periods whose *end* is today or later
    // so an in-progress absence still surfaces.
    return groupDatesToPeriods(list).filter((p) => p.end >= today);
}

export default function AvailabilityView({ canManage, eyebrow, backFallback, title }) {
    const [engineers, setEngineers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [editing, setEditing] = useState(null);
    const [palette, setPalette] = useState([]);

    const refresh = useCallback(async () => {
        setRefreshing(true);
        setErrored(false);
        try {
            const list = await api.listEngineers({ includeArchived: false });
            setEngineers(list);
        } catch (e) {
            setEngineers([]);
            setErrored(true);
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load engineers");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        if (canManage) {
            api.engineerPalette().then(setPalette).catch(() => setPalette([]));
        }
    }, [refresh, canManage]);

    const sorted = useMemo(
        () => [...(Array.isArray(engineers) ? engineers : [])].sort((a, b) => (a.displayName || "").localeCompare(b.displayName || "")),
        [engineers],
    );

    return (
        <>
            <PageContainer width="medium" testid="engineer-availability-page">
                <PageHeader
                    eyebrow={eyebrow || "Operations · Engineers · Availability"}
                    title={title || "Engineer availability"}
                    subtitle="Working days and upcoming holidays for every active resource."
                    backFallback={backFallback || "/operations"}
                    testid="engineers-availability-header"
                    rightSlot={(
                        <button
                            onClick={() => refresh()}
                            data-testid="availability-refresh"
                            aria-label="Refresh availability"
                            className="inline-flex items-center justify-center h-11 w-11 rounded-md border border-[#E5E7EB] bg-white text-[#4B5565] hover:bg-[#F5F5F7] disabled:opacity-60"
                            disabled={refreshing}
                        >
                            <RefreshCw size={16} strokeWidth={1.75} className={refreshing ? "animate-spin" : ""} />
                        </button>
                    )}
                />

                {loading ? (
                    <div className="mt-6 p-10 text-center text-sm text-[#4B5565]" data-testid="availability-loading">Loading…</div>
                ) : errored ? (
                    <div className="mt-6 p-6 rounded-lg border border-[#F59E0B]/35 bg-[#FEF3C7]/60 text-sm text-[#7C2D12]" data-testid="availability-error">
                        Couldn't load availability. Please retry.
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="mt-6 p-10 text-center" data-testid="availability-empty">
                        <CalendarIcon size={32} strokeWidth={1.5} className="mx-auto text-[#D1D5DB]" />
                        <div className="mt-2 text-base text-[#131B2F] font-medium">No active resources</div>
                        <div className="text-sm text-[#4B5565] mt-1">Add a resource from Field Team Management first.</div>
                    </div>
                ) : (
                    <ul className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="availability-list">
                        {sorted.map((eng) => (
                            <AvailabilityCard
                                key={eng.id}
                                eng={eng}
                                canManage={canManage}
                                onEdit={() => setEditing(eng)}
                            />
                        ))}
                    </ul>
                )}
            </PageContainer>

            <EngineerForm
                open={!!editing && canManage}
                engineer={editing}
                palette={palette}
                mode="availability"
                onClose={() => setEditing(null)}
                onSaved={(saved) => {
                    setEngineers((prev) => prev.map((x) => (x.id === saved.id ? saved : x)));
                    setEditing(null);
                    refresh();
                }}
            />
        </>
    );
}

export function AvailabilityCard({ eng, canManage, onEdit }) {
    // DEV-024 · Card mirrors the editor exactly — same day states,
    // same colour palette, same weekly total. Uses the shared
    // `availabilityPattern` helper so the card cannot drift from
    // the editor.
    const periods = upcomingHolidayPeriods(eng.holidays);
    const weeklyHours = weeklyHoursFor(eng);
    return (
        <li
            className="rounded-xl border border-[#E5E7EB] bg-white p-4"
            data-testid={`availability-row-${eng.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-base font-medium text-[#131B2F] truncate">
                        {eng.displayName}
                    </div>
                    <div className="text-xs text-[#4B5565] mt-0.5">{effectiveResourceType(eng)}</div>
                </div>
                {canManage ? (
                    <button
                        onClick={onEdit}
                        data-testid={`availability-edit-${eng.id}`}
                        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-[#E5E7EB] text-xs font-medium text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        <Pencil size={12} strokeWidth={1.75} /> Edit
                    </button>
                ) : (
                    <span
                        data-testid={`availability-readonly-${eng.id}`}
                        className="text-[11px] text-[#4B5565]"
                    >
                        View only
                    </span>
                )}
            </div>

            <div className="mt-3">
                {/* DEV-024 · Working Days heading + weekly total.
                    Wraps cleanly on tablet/narrower widths so nothing
                    clips. The total represents the NORMAL weekly
                    working pattern — holidays/blackouts/appointments
                    are NOT subtracted. */}
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-1.5">
                    <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565]">
                        Working days
                    </div>
                    <div
                        className="text-[11px] text-[#4B5565]"
                        data-testid={`availability-weekly-hours-${eng.id}`}
                    >
                        Weekly working hours:{" "}
                        <span
                            className="font-semibold text-[#131B2F] tabular-nums"
                            data-testid={`availability-weekly-hours-value-${eng.id}`}
                        >
                            {weeklyHours}
                        </span>{" "}
                        hrs
                    </div>
                </div>
                <div
                    className="flex gap-1.5 flex-wrap"
                    data-testid={`availability-days-${eng.id}`}
                >
                    {DAY_LABELS.map((label, idx) => {
                        const day = idx + 1; // ISO Mon=1 … Sun=7
                        const state = stateForDay(eng, day);
                        const style = DAY_STATE_STYLE[state];
                        return (
                            <span
                                key={label}
                                data-testid={`availability-day-${eng.id}-${day}`}
                                data-state={state}
                                aria-label={`${label} · ${style.longLabel} (${style.hours} hours)`}
                                className={`inline-flex items-center gap-1.5 min-w-[64px] h-8 px-2 rounded-md text-xs font-medium ${style.cardCls}`}
                            >
                                <span>{label}</span>
                                <span
                                    className={`text-[10px] uppercase tracking-wide ${style.accentCls}`}
                                    data-testid={`availability-day-${eng.id}-${day}-label`}
                                >
                                    {style.label}
                                </span>
                            </span>
                        );
                    })}
                </div>
            </div>

            <div className="mt-3">
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#4B5565] mb-1.5">
                    Upcoming holidays
                </div>
                {periods.length === 0 ? (
                    <div
                        className="text-xs text-[#4B5565]"
                        data-testid={`availability-holidays-empty-${eng.id}`}
                    >
                        No upcoming holidays booked.
                    </div>
                ) : (
                    <ul
                        className="flex gap-1.5 flex-wrap"
                        data-testid={`availability-holidays-${eng.id}`}
                    >
                        {periods.slice(0, 8).map((p) => {
                            const key = periodKey(p);
                            return (
                                <li
                                    key={key}
                                    className="inline-flex items-center gap-1 h-8 px-2 rounded-md bg-[#F5F5F7] text-xs text-[#131B2F] border border-[#E5E7EB]"
                                    data-testid={`availability-holiday-period-${eng.id}-${key}`}
                                >
                                    <CalendarIcon size={11} strokeWidth={1.75} /> {formatPeriod(p)}
                                </li>
                            );
                        })}
                        {periods.length > 8 && (
                            <li className="text-xs text-[#4B5565] px-2 self-center">
                                +{periods.length - 8} more
                            </li>
                        )}
                    </ul>
                )}
            </div>

            <div className="mt-3 flex gap-2 text-xs">
                <Link
                    to={`/operations/calendar?engineer_filter=${encodeURIComponent(eng.id)}&focus=week`}
                    data-testid={`availability-calendar-link-${eng.id}`}
                    className="text-[#131B2F] hover:underline"
                >
                    Open in calendar
                </Link>
            </div>
        </li>
    );
}
